import { PageButtonProps } from './Pagination';

const calculateRange = (current: number, total: number): [number, number] => {
  if (current <= 4) {
    // if current page is 1, 2, 3, or 4, show pages 2, 3, 4, and 5
    return [2, Math.min(5, total - 1)];
  }

  if (current >= total - 3) {
    // if current page is the last 3 pages, show the last 3 pages
    return [Math.max(total - 4, 2), total - 1];
  }

  // if current page is in the middle, show the current page - 1 and current page + 1
  return [current - 1, current + 1];
};

/**
 * Returns the visible pages for the pagination component. Will be displayed like
 *
 * 1 ... 6 7 8 ... 200
 */
export const getVisiblePages = (current: number, total: number): PageButtonProps['display'][] => {
  if (total <= 1) {
    return [];
  }

  // first page is always shown
  const firstPage: PageButtonProps['display'][] = [{ type: 'PAGE_NUMBER', pageNumber: 1 }];

  // pre dots (if current page is greater than 4)
  const preDots: PageButtonProps['display'][] =
    current > 4 && total > 6 ? [{ type: 'ELLIPSIS' }] : [];

  // post dots (if current page is less than last page - 3)
  const postDots: PageButtonProps['display'][] =
    current < total - 3 && total > 6 ? [{ type: 'ELLIPSIS' }] : [];

  // last page is always shown
  const lastPage: PageButtonProps['display'][] =
    total > 1 ? [{ type: 'PAGE_NUMBER', pageNumber: total }] : [];

  // range numbers are what are dynamically shown
  const [rangeStart, rangeEnd] = calculateRange(current, total);
  const rangeNumbers = Array.from(
    { length: rangeEnd - rangeStart + 1 },
    (_, index) => rangeStart + index,
  );

  const rangeNumbersDisplay: PageButtonProps['display'][] = rangeNumbers.map((number) => ({
    type: 'PAGE_NUMBER',
    pageNumber: number,
  }));

  return [...firstPage, ...preDots, ...rangeNumbersDisplay, ...postDots, ...lastPage];
};
